// src/features/admin/components/settings/document-management/components/font-manager/FontRegistry.tsx
import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  ClockIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { getAllFonts, updateFontStatus, syncFontManifest, type RealmFont } from '@/features/wordprocessing/services/FontAPI';

// Grouped font family interface
interface FontFamily {
  familyName: string;
  variants: RealmFont[];
  enabledCount: number;
  totalCount: number;
  category: string;
  courtCompliant: boolean;
  isCustom: boolean;
}

/**
 * Font Registry Component
 * 
 * Displays all available fonts with enable/disable controls,
 * search/filter capabilities, and font preview.
 */
const FontRegistry: React.FC = () => {
  const [fonts, setFonts] = useState<RealmFont[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterCourtCompliant, setFilterCourtCompliant] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());

  // Load fonts from backend on mount
  useEffect(() => {
    loadFonts();
  }, []);

  const loadFonts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const fontsData = await getAllFonts();
      setFonts(fontsData);
      
      if (fontsData.length === 0) {
        setError('No fonts found. Click "Sync Fonts from Manifest" to load fonts.');
      }
    } catch (error: any) {
      console.error('Failed to load fonts:', error);
      setError(error.message || 'Failed to load fonts');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncFonts = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSuccessMessage(null);
      
      const result = await syncFontManifest();
      console.log('Sync result:', result);
      
      // Reload fonts after sync
      await loadFonts();
      
      setSuccessMessage(`Successfully synced ${result.totalFonts} fonts! ${result.newFonts} new, ${result.existingFonts} existing.`);
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error: any) {
      console.error('Failed to sync fonts:', error);
      setError(error.message || 'Failed to sync fonts');
    } finally {
      setSyncing(false);
    }
  };

  const toggleFontEnabled = async (fontUid: string, currentlyEnabled: boolean) => {
    try {
      // Optimistically update UI
      setFonts(prevFonts => 
        prevFonts.map(font =>
          font.fontUid === fontUid
            ? { ...font, enabled: !currentlyEnabled }
            : font
        )
      );
      
      // Call backend API (pass fontUid, not database ID)
      await updateFontStatus(fontUid, !currentlyEnabled);
      
      console.log(`Updated font ${fontUid} to ${!currentlyEnabled ? 'enabled' : 'disabled'}`);
    } catch (error: any) {
      console.error('Failed to update font status:', error);
      
      // Revert on error
      setFonts(prevFonts => 
        prevFonts.map(font =>
          font.fontUid === fontUid
            ? { ...font, enabled: currentlyEnabled }
            : font
        )
      );
      
      setError('Failed to update font status. Please try again.');
    }
  };

  // Filter fonts based on search and filters
  const filteredFonts = fonts.filter(font => {
    // Defensive check for undefined values
    const displayName = font.displayName || '';
    const family = font.fontFamily || '';
    
    const matchesSearch = displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         family.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = filterCategory === 'all' || font.category === filterCategory;
    
    const matchesCourtCompliant = filterCourtCompliant === 'all' ||
      (filterCourtCompliant === 'compliant' && font.courtCompliant) ||
      (filterCourtCompliant === 'non-compliant' && !font.courtCompliant);

    return matchesSearch && matchesCategory && matchesCourtCompliant;
  });

  // Group fonts by family
  const fontFamilies = React.useMemo(() => {
    const familyMap = new Map<string, FontFamily>();
    
    filteredFonts.forEach(font => {
      const family = font.fontFamily || 'Unknown';
      
      if (!familyMap.has(family)) {
        familyMap.set(family, {
          familyName: family,
          variants: [],
          enabledCount: 0,
          totalCount: 0,
          category: font.category,
          courtCompliant: font.courtCompliant,
          isCustom: font.isCustom
        });
      }
      
      const familyData = familyMap.get(family)!;
      familyData.variants.push(font);
      familyData.totalCount++;
      if (font.enabled) familyData.enabledCount++;
    });
    
    // Sort families alphabetically
    return Array.from(familyMap.values()).sort((a, b) => 
      a.familyName.localeCompare(b.familyName)
    );
  }, [filteredFonts]);

  // Toggle family expansion
  const toggleFamily = (familyName: string) => {
    setExpandedFamilies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(familyName)) {
        newSet.delete(familyName);
      } else {
        newSet.add(familyName);
      }
      return newSet;
    });
  };

  // Enable/disable all variants in a family
  const toggleFamilyEnabled = async (family: FontFamily, enable: boolean) => {
    try {
      // Update all variants
      await Promise.all(
        family.variants.map(variant => 
          updateFontStatus(variant.fontUid, enable)
        )
      );
      
      // Reload fonts
      await loadFonts();
      
      setSuccessMessage(`${enable ? 'Enabled' : 'Disabled'} all ${family.totalCount} variant(s) of ${family.familyName}`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error: any) {
      console.error('Failed to update family status:', error);
      setError(`Failed to ${enable ? 'enable' : 'disable'} ${family.familyName} family`);
    }
  };

  const stats = {
    total: fonts.length,
    enabled: fonts.filter(f => f.enabled).length,
    courtCompliant: fonts.filter(f => f.courtCompliant).length,
    families: fontFamilies.length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lex-accent mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading fonts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="font-registry">
      {/* Success Message Banner */}
      {successMessage && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-3" />
            <p className="text-sm text-green-800 dark:text-green-300">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error/Empty State Banner */}
      {error && (
        <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <DocumentTextIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-3" />
              <p className="text-sm text-yellow-800 dark:text-yellow-300">{error}</p>
            </div>
            <button
              onClick={handleSyncFonts}
              disabled={syncing}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-lex-accent-contrast bg-lex-accent hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Fonts from Manifest'}
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Font Families</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.families}</p>
            </div>
            <DocumentTextIcon className="h-8 w-8 text-lex-accent" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Variants</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.total}</p>
            </div>
            <DocumentTextIcon className="h-8 w-8 text-gray-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Enabled</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{stats.enabled}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Court Compliant</p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">{stats.courtCompliant}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters</h3>
          <button
            onClick={handleSyncFonts}
            disabled={syncing}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Fonts'}
          </button>
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-4 md:space-y-0">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search fonts by name or family..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-transparent"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex items-center space-x-2">
            <AdjustmentsHorizontalIcon className="h-5 w-5 text-gray-400" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="serif">Serif</option>
              <option value="sans-serif">Sans-Serif</option>
              <option value="monospace">Monospace</option>
            </select>
          </div>

          {/* Court Compliant Filter */}
          <div>
            <select
              value={filterCourtCompliant}
              onChange={(e) => setFilterCourtCompliant(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
            >
              <option value="all">All Fonts</option>
              <option value="compliant">Court Compliant</option>
              <option value="non-compliant">Non-Compliant</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        Showing {fontFamilies.length} font {fontFamilies.length === 1 ? 'family' : 'families'} ({filteredFonts.length} variants)
      </div>

      {/* Fonts Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-8">
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Font Family / Variant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Preview
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Variants / Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {fontFamilies.map((family) => {
                const isExpanded = expandedFamilies.has(family.familyName);
                const allEnabled = family.enabledCount === family.totalCount;
                const someEnabled = family.enabledCount > 0 && family.enabledCount < family.totalCount;
                
                return (
                  <React.Fragment key={family.familyName}>
                    {/* Family Header Row */}
                    <tr className="bg-gray-50 dark:bg-gray-750 hover:bg-gray-100 dark:hover:bg-gray-700">
                      <td className="px-4 py-4">
                        <button
                          onClick={() => toggleFamily(family.familyName)}
                          className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        >
                          {isExpanded ? (
                            <ChevronDownIcon className="h-5 w-5" />
                          ) : (
                            <ChevronRightIcon className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            {family.familyName}
                            {family.isCustom && (
                              <span className="ml-2 px-2 py-0.5 text-xs bg-lex-accent-soft dark:bg-lex-accent-soft text-lex-accent dark:text-lex-accent rounded">
                                Custom
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div 
                          className="text-sm text-gray-900 dark:text-gray-100"
                          style={{ fontFamily: family.familyName }}
                        >
                          The quick brown fox
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                          {family.category}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="text-gray-900 dark:text-white font-medium">
                            {family.totalCount} variant{family.totalCount !== 1 ? 's' : ''}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {family.enabledCount} enabled
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          {!allEnabled && (
                            <button
                              onClick={() => toggleFamilyEnabled(family, true)}
                              className="inline-flex items-center px-3 py-1.5 border border-green-300 dark:border-green-600 rounded-md text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                            >
                              <EyeIcon className="h-3.5 w-3.5 mr-1" />
                              Enable All
                            </button>
                          )}
                          {(allEnabled || someEnabled) && (
                            <button
                              onClick={() => toggleFamilyEnabled(family, false)}
                              className="inline-flex items-center px-3 py-1.5 border border-red-300 dark:border-red-600 rounded-md text-xs font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                            >
                              <EyeSlashIcon className="h-3.5 w-3.5 mr-1" />
                              Disable All
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    
                    {/* Variant Rows (shown when expanded) */}
                    {isExpanded && family.variants.map((variant) => (
                      <tr key={variant.id} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750">
                        <td className="px-4 py-3"></td>
                        <td className="px-6 py-3">
                          <div className="pl-6">
                            <div className="text-sm text-gray-700 dark:text-gray-300">
                              {variant.displayName}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {variant.weight} • {variant.style}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3">
                          <div 
                            className="text-sm text-gray-900 dark:text-gray-100"
                            style={{ 
                              fontFamily: variant.fontFamily,
                              fontWeight: variant.weight,
                              fontStyle: variant.style
                            }}
                          >
                            The quick brown fox
                          </div>
                        </td>
                        <td className="px-6 py-3">
                          {variant.courtCompliant && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300">
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Court
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-3">
                          {variant.enabled ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300">
                              <EyeIcon className="h-3 w-3 mr-1" />
                              Enabled
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                              <EyeSlashIcon className="h-3 w-3 mr-1" />
                              Disabled
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-3 text-right">
                          <button
                            onClick={() => toggleFontEnabled(variant.fontUid, variant.enabled)}
                            className={`inline-flex items-center px-2.5 py-1 border rounded-md text-xs font-medium transition-colors ${
                              variant.enabled
                                ? 'border-red-300 dark:border-red-600 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30'
                                : 'border-green-300 dark:border-green-600 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30'
                            }`}
                          >
                            {variant.enabled ? 'Disable' : 'Enable'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {fontFamilies.length === 0 && (
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No font families found</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Try adjusting your search or filter criteria, or upload new fonts.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Info Note */}
      <div className="mt-6 bg-lex-accent-soft dark:bg-lex-accent-soft border border-lex-accent dark:border-lex-accent rounded-lg p-4">
        <div className="flex">
          <ClockIcon className="h-5 w-5 text-lex-accent mr-3 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-lex-accent dark:text-lex-accent">
            <p className="font-medium mb-1">About Font Management</p>
            <p>
              Only enabled fonts will appear in the EdgeDocs toolbar dropdown for document editing. 
              Court-compliant fonts are recommended for legal documents requiring specific formatting standards.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FontRegistry;

